import HCaptcha from '@hcaptcha/react-hcaptcha';
import {useMemo, useRef, useState} from 'react';
import {FormProvider} from 'react-hook-form';
import {Link, useParams} from 'react-router';

import {AccountTypeT} from '@/lib/types/authentication.types';

import {FormIds} from '@/lib/forms/form-ids';
import {signUpFormFields, signUpFormSteps} from '@/lib/forms/sign-up-form-config';
import {getBaseURL} from '@/lib/helpers/urlHelpers';

import {useAuth} from '@/providers/components/authProvider';
import {useAppForm} from '@/providers/components/formProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import Button from '@/components/Button';
import FormFields from '@/components/form/FormFields';
import FormGlobalError from '@/components/form/FormGlobalError';
import FormSubmitButton from '@/components/form/FormSubmitButton';

type SignUpFormDataT = {
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
};

function SignUpPage() {
  // const navigate = useNavigate();
  const {toast} = useNotifications();
  const {from} = useParams<{from: AccountTypeT | undefined}>();
  const accountType = from ?? 'speculator';

  const {
    emailVerificationSent,
    handleSignOut,
    handleEmailAndPasswordSignUp,
    handleGoogleAuthentication,
    handleFacebookAuthentication,
    handleResendEmailVerification,
  } = useAuth();

  // State
  const [captchaToken, setCaptchaToken] = useState<string | undefined>();

  // Refs
  const captchaRef = useRef<HCaptcha | null>(null);

  // Computed
  const registerAsMessage = useMemo(() => {
    let signUpMessage;

    switch (from?.toLowerCase()) {
      case 'brand':
        signUpMessage = 'Register a Brand';
        break;
      case 'distributor':
        signUpMessage = 'Register a Distributor';
        break;
      case 'retailer':
        signUpMessage = 'Register a Retailer';
        break;
      default:
        signUpMessage = 'Sign Up';
    }

    return signUpMessage;
  }, [from]);

  // Create the form in the form provider
  const {
    formConfig,
    setGlobalError,
    setLoading,
    // state: {loading},
  } = useAppForm<SignUpFormDataT>({
    formId: FormIds.SIGN_UP,
    config: {
      fields: signUpFormFields,
      steps: signUpFormSteps,
    },
  });

  // React Hook Form Config
  const {
    handleSubmit,
    trigger,
    formState: {errors},
    reset,
    getValues,
  } = formConfig;

  // Submit handler
  const onSubmit = async (data: SignUpFormDataT) => {
    if (Object.keys(errors).length) {
      setGlobalError('Something went wrong. Please check the form for errors');
      trigger();
      return;
    }

    if (!captchaToken) {
      setGlobalError('Please complete the CAPTCHA verification');
      return;
    }

    try {
      setLoading(true);
      await handleEmailAndPasswordSignUp({email: data.email, password: data.password, captchaToken});
      toast.success('User registered successfully, check your email for verification');
      reset();
    } catch (error) {
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
      reset({email: data.email});
    } finally {
      captchaRef.current?.resetCaptcha();
      setLoading(false);
    }
  };

  const handleGoogleAuth = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      reset();
      setLoading(true);
      await handleGoogleAuthentication(from);
    } catch (error) {
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleFacebookAuth = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      reset();
      setLoading(true);
      await handleFacebookAuthentication(from);
    } catch (error) {
      setGlobalError((error as Error).message ?? 'Something went wrong. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // If email verification has been sent, show the verification screen
  if (emailVerificationSent) {
    return (
      <div className="container">
        <div className="authentication authentication-basic flex h-full items-center justify-center text-defaultsize text-defaulttextcolor">
          <div className="d-flex grid-cols-6 justify-center">
            <div className="col-span-6 sm:col-span-8 md:col-span-6 lg:col-span-4 xl:col-span-4 xxl:col-span-4">
              <div className="mb-[1rem] mt-[2.5rem] flex justify-center">
                <Link to={getBaseURL()} tabIndex={-1}>
                  <img src={'/images/logo/logo.png'} alt="logo" className="desktop-logo max-w-[150px]" />
                  <img src={'/images/logo/logo.png'} alt="logo" className="desktop-dark max-w-[150px]" />
                </Link>
              </div>
              <div className="box max-w-[400px]">
                <div className="box-body !p-[1rem]">
                  <p className="h5 mb-2 text-center font-semibold">Email Verification Sent</p>
                  <p className="mb-4 text-center font-normal text-[#8c9097] opacity-[0.7] dark:text-white/50">
                    {`We have sent you an email to verify your email address to ${getValues('email') ?? 'the supplied email address'}.`}
                  </p>
                  <p className="text-center text-2xl font-semibold leading-snug text-slate-200 underline underline-offset-2">
                    Please check your email and verify it to continue
                  </p>

                  {/*  if the user has not received the email, they can click the button to resend the email */}
                  <div className="mt-5 text-center">
                    <p className="mb-2 text-center font-normal text-[#8c9097] opacity-[0.7] dark:text-white/50">
                      If you have not received the verification email,
                      <br /> you can resend it.
                    </p>
                    <Button variant="solid" color="dark" onClick={() => handleResendEmailVerification()} icon="refresh">
                      Resend Email Verification
                    </Button>
                  </div>
                </div>
              </div>
              <div className="text-center">
                <p className="mt-4 text-center text-[0.75rem] text-[#8c9097] dark:text-white/50">
                  Need some time still?{' '}
                  <button onClick={handleSignOut} className="text-primary">
                    Sign Out
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="authentication authentication-basic h-full items-center text-defaultsize text-defaulttextcolor">
        <div className="mx-auto max-w-lg">
          <div className="">
            <div className="my-[2.5rem] flex justify-center">
              <Link to={getBaseURL()} tabIndex={-1}>
                <img src={'/images/logo/logo.png'} alt="logo" className="desktop-logo max-w-[150px]" />
                <img src={'/images/logo/logo.png'} alt="logo" className="desktop-dark max-w-[150px]" />
              </Link>
            </div>
            <div className="box">
              <div className="box-body !p-[3rem]">
                <h1 className="h3 mb-2 text-center font-semibold md:h1" id="signup-heading">
                  {registerAsMessage}
                </h1>
                {accountType === 'speculator' && (
                  <p
                    className="mb-2 text-center text-lg leading-5 text-gray-800/70 dark:text-white/40"
                    id="register-as-text"
                  >
                    Have a Company? Need to register as
                    <br />a{' '}
                    <Link to="/sign-up/brand" className="text-primary underline" aria-label="Register as a Brand">
                      Brand
                    </Link>
                    ,{' '}
                    <Link to="/sign-up/retailer" className="text-primary underline" aria-label="Register as a Retailer">
                      Retailer
                    </Link>
                    , or{' '}
                    <Link
                      to="/sign-up/distributor"
                      className="text-primary underline"
                      aria-label="Register as a Distributor"
                    >
                      Distributor
                    </Link>
                    ?
                  </p>
                )}
                <small className="mb-5 block text-center text-gray-800/70 dark:text-white/40" id="signup-terms-text">
                  By signing up you are agreeing to our{' '}
                  <Link
                    to="https://tos.tnc.exchange/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Terms and Conditions (opens in new tab)"
                  >
                    <u>Terms &amp; Conditions</u>
                  </Link>
                  <span className="mx-1">and</span>
                  <Link
                    to="https://pp.tnc.exchange/"
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Privacy Policy (opens in new tab)"
                  >
                    <u>Privacy Policy</u>
                  </Link>
                  .
                </small>

                <div className="flex flex-col gap-2 text-center" role="group" aria-labelledby="social-login-heading">
                  <span id="social-login-heading" className="sr-only">
                    Sign up with social media
                  </span>
                  <Button onClick={handleFacebookAuth} aria-label="Sign up with Facebook" color="facebook" width="full">
                    Sign Up Facebook
                  </Button>
                  <Button onClick={handleGoogleAuth} aria-label="Sign up with Google" width="full" color="google">
                    Sign Up Google
                  </Button>
                </div>

                <div className="authentication-barrier my-4 text-center" aria-hidden="true">
                  <span className="text-xs text-gray-800/80 dark:text-white/40">Or Sign Up Manually</span>
                </div>

                <FormProvider {...formConfig}>
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-4"
                    aria-labelledby="signup-heading"
                    role="form"
                  >
                    <FormGlobalError formId={FormIds.SIGN_UP} />
                    <FormFields formId={FormIds.SIGN_UP} />
                    <div className="flex justify-center">
                      <HCaptcha
                        ref={captchaRef}
                        sitekey={import.meta.env.VITE_HCAPTCHA_SITE_KEY}
                        onVerify={token => {
                          setCaptchaToken(token);
                        }}
                        onExpire={() => {
                          setCaptchaToken(undefined);
                        }}
                      />
                    </div>
                    <FormSubmitButton formId={FormIds.SIGN_UP} label="Create Account" showStepCount={false} />
                  </form>
                </FormProvider>

                <div className="text-center">
                  <p className="mt-4 text-[0.75rem] text-[#8c9097] dark:text-white/50">
                    Already have an account?{' '}
                    <Link
                      to={`${getBaseURL()}/sign-in`}
                      className="text-primary"
                      aria-label="Sign in to existing account"
                    >
                      Sign In
                    </Link>
                  </p>
                </div>
              </div>
            </div>
            <div className="mb-4 flex items-center justify-center gap-1">
              <Link
                to="https://tos.tnc.exchange"
                className="text-success"
                aria-label="View Terms and Conditions"
                target="_blank"
                rel="noreferrer"
              >
                <u>Terms &amp; Conditions</u>
              </Link>
              <span>|</span>
              <Link
                to="https://pp.tnc.exchange"
                className="text-success"
                aria-label="View Privacy Policy"
                target="_blank"
                rel="noreferrer"
              >
                <u>Privacy Policy</u>
              </Link>
            </div>
            <small className="my-5 block text-center text-gray-800/70">
              By creating an account you agree to our
              <br />
              Terms &amp; Conditions and Privacy Policy
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignUpPage;
